/**
 * Log / edit a sleep session for a specific date. Used to live inline at
 * the top of the Sleep screen — moved into a modal so the landing view
 * leads with trends. Sleep data mostly arrives via Sleep Cycle imports;
 * manual entry is the exception.
 *
 * The modal upserts by date — opening for "today" auto-prefills with the
 * existing session (if any) so the user can refine numbers after the fact.
 */

import { useEffect, useState } from 'react';
import { useSleepSessions, useUpsertSleep, type SleepSessionDto } from '../api/health';
import { FormModal } from './FormModal';
import styles from './FormModal.module.css';

interface SleepFormModalProps {
  open: boolean;
  onClose: () => void;
  /** YYYY-MM-DD. Defaults to today when omitted. */
  date?: string | undefined;
}

export function SleepFormModal({ open, onClose, date: dateProp }: SleepFormModalProps) {
  const sessions = useSleepSessions();
  const upsert = useUpsertSleep();

  const [date, setDate] = useState<string>(dateProp ?? today());
  const [totalH, setTotalH] = useState<string>('');
  const [deepH, setDeepH] = useState<string>('');
  const [remH, setRemH] = useState<string>('');
  const [score, setScore] = useState<string>('');
  const [bedtime, setBedtime] = useState<string>('');
  const [wakeTime, setWakeTime] = useState<string>('');
  const [notes, setNotes] = useState<string>('');

  // When the modal opens, pre-fill from the existing session for that date
  // (if any). This means the user gets edit-in-place behavior for free
  // without a separate "edit" entry point.
  useEffect(() => {
    if (!open) return;
    const targetDate = dateProp ?? today();
    setDate(targetDate);
    const existing = (sessions.data?.sessions ?? []).find(
      (s) => s.date === targetDate,
    );
    fillFromSession(existing ?? null);
    upsert.reset();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, dateProp, sessions.data?.sessions]);

  function fillFromSession(s: SleepSessionDto | null) {
    setTotalH(s?.totalH?.toString() ?? '');
    setDeepH(s?.deepH?.toString() ?? '');
    setRemH(s?.remH?.toString() ?? '');
    setScore(s?.score?.toString() ?? '');
    setBedtime(s?.bedtime ?? '');
    setWakeTime(s?.wakeTime ?? '');
    setNotes(s?.notes ?? '');
  }

  const pending = upsert.isPending;
  const error = (upsert.error as Error | null)?.message ?? null;
  // A session is meaningful if the user provided at least one numeric or
  // time field — otherwise saving would just stamp an empty row.
  const valid =
    date.trim() !== '' &&
    (totalH.trim() !== '' ||
      deepH.trim() !== '' ||
      remH.trim() !== '' ||
      score.trim() !== '' ||
      bedtime !== '' ||
      wakeTime !== '');

  const submit = () => {
    if (!valid || pending) return;
    upsert.mutate(
      {
        date,
        patch: {
          totalH: toNum(totalH),
          deepH: toNum(deepH),
          remH: toNum(remH),
          score: toNum(score),
          bedtime: bedtime || null,
          wakeTime: wakeTime || null,
          notes,
        },
      },
      { onSuccess: () => onClose() },
    );
  };

  return (
    <FormModal
      open={open}
      onClose={onClose}
      title={dateProp ? 'Edit sleep' : 'Log sleep'}
      subtitle="One night's totals — manual entry. Imports go through Data loads."
      submitLabel={pending ? 'Saving…' : 'Save sleep'}
      submitDisabled={!valid || pending}
      onSubmit={submit}
      error={error}
    >
      <div className={styles.field}>
        <label className={styles.fieldLabel} htmlFor="sleep-date">Date</label>
        <input
          id="sleep-date"
          className={styles.input}
          type="date"
          value={date}
          onChange={(e) => setDate(e.target.value)}
        />
      </div>

      <div className={styles.field}>
        <label className={styles.fieldLabel} htmlFor="sleep-total">Total (h)</label>
        <input
          id="sleep-total"
          className={styles.input}
          type="number"
          min="0"
          step="0.1"
          value={totalH}
          onChange={(e) => setTotalH(e.target.value)}
        />
      </div>

      <div className={styles.field}>
        <label className={styles.fieldLabel} htmlFor="sleep-deep">Deep (h)</label>
        <input
          id="sleep-deep"
          className={styles.input}
          type="number"
          min="0"
          step="0.1"
          value={deepH}
          onChange={(e) => setDeepH(e.target.value)}
        />
      </div>

      <div className={styles.field}>
        <label className={styles.fieldLabel} htmlFor="sleep-rem">REM (h)</label>
        <input
          id="sleep-rem"
          className={styles.input}
          type="number"
          min="0"
          step="0.1"
          value={remH}
          onChange={(e) => setRemH(e.target.value)}
        />
      </div>

      <div className={styles.field}>
        <label className={styles.fieldLabel} htmlFor="sleep-score">Score (0–100)</label>
        <input
          id="sleep-score"
          className={styles.input}
          type="number"
          min="0"
          max="100"
          step="1"
          value={score}
          onChange={(e) => setScore(e.target.value)}
        />
      </div>

      <div className={styles.field}>
        <label className={styles.fieldLabel} htmlFor="sleep-bed">Bedtime</label>
        <input
          id="sleep-bed"
          className={styles.input}
          type="time"
          value={bedtime}
          onChange={(e) => setBedtime(e.target.value)}
        />
      </div>

      <div className={styles.field}>
        <label className={styles.fieldLabel} htmlFor="sleep-wake">Wake time</label>
        <input
          id="sleep-wake"
          className={styles.input}
          type="time"
          value={wakeTime}
          onChange={(e) => setWakeTime(e.target.value)}
        />
      </div>

      <div className={styles.field}>
        <label className={styles.fieldLabel} htmlFor="sleep-notes">Notes</label>
        <input
          id="sleep-notes"
          className={styles.input}
          value={notes}
          onChange={(e) => setNotes(e.target.value)}
        />
      </div>
    </FormModal>
  );
}

function today(): string {
  return new Date().toISOString().slice(0, 10);
}

function toNum(s: string): number | null {
  if (s.trim() === '') return null;
  const n = Number(s);
  return Number.isFinite(n) ? n : null;
}
